/**
 * Reducer for setting validation properties on form elements and pages.
 * This closure allows us to reuse the reducer for multiple forms and pages as it allows us
 * to supply the initialState as an argument.
 * We can then use the returned, closured reducer in combineReducers.
 * @param  {Object} initialState - The initial version of the state that this reducer has access to
 * @param  {ValidationModel} validationModel  - The validation model for the form.
 * @param  {String} stateKey - The stateKey that this reducer is responsible for
 * @param  {Object} customCases  Any app specific reducer cases to be handled at the validation reducer level.
 * @returns {Function} validationReducer - The validation reducer
 */

const allowedActions = [
    'ELEMENT_TOUCHED',
    'VALIDATE_PAGE',
    'RESET_TOUCHES',
    'RESET_PAGE_VALIDATION',
    'SET_VALIDATION'
];

const createValidationReducer = (initialState, validationModel, stateKey, customCases = {}) => {
    return (state = initialState, action) => {
        if (allowedActions.includes(action.type) && !action.stateKey) {
            throw new Error(`Action sent to validation reducer has no stateKey: ${JSON.stringify(action)}`);
        }
        if (action.stateKey !== stateKey) {
            return state;
        }
        switch (action.type) {
            case 'ELEMENT_TOUCHED': {
                /**
                 * Call the validation model to validate action.value as a new value
                 * for action.element.
                 */
                return state.updateIn(['touches', action.identifier], (value) => true);
            }
            case 'VALIDATE_PAGE': {
                /**
                 * Validate the page based on the validation status of it's elements.
                 */
                const { pageName, rules, visibility } = action;
                const { touches, isValid, invalidElements } = validationModel.validatePage(pageName, rules, visibility);

                /**
                 * Set the pages validation status.
                 */
                const updatedState = { touches: {}, pages: { [pageName]: isValid }, invalidElements };
                for (const name of Object.keys(touches)) {
                    updatedState.touches[name] = touches[name];
                }
                const newState = state.setIn(['invalidElements'], invalidElements);
                return newState.mergeDeep(updatedState);
            }
            case 'RESET_TOUCHES': {
                const resetTouches = { touches: {} };
                if (action.identifiers) {
                    const identifiers = action.identifiers;
                    resetTouches.touches =                        identifiers.reduce((acc, val) => {
                        acc[val] = false;
                        return acc;
                    }, {});
                }
                if (action.baseIdentifier) {
                    for (const id of state.get('touches').keySeq()) {
                        if (id.indexOf(action.baseIdentifier) >= 0) {
                            resetTouches.touches[id] = false;
                        }
                    }
                }
                return state.mergeDeep(resetTouches);
            }
            case 'RESET_PAGE_VALIDATION': {
                return state.setIn(['pages', action.page], false);
            }
            case 'SET_OUTSIDE_FORM_VALIDATION': {
                return state.setIn(['outsideForm', action.identifier], action.value);
            }
            default: {
                // Vertical specific custom cases
                for (const customCase of Object.keys(customCases)) {
                    if (action.type === customCase) {
                        return customCases[customCase](state, action);
                    }
                }

                // Supplied action not handled, return the state.
                // We need this to handle the @@redux/init actions that combineReducers
                // fires to check the reducer for bad patterns, thisa won't be hit
                // during normal operation.
                return state;
            }
        }
    };
};

export { createValidationReducer };
